import { Alert, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import { Info as InfoIcon } from "@mui/icons-material";
import { isWithinInterval } from "date-fns";
import de from "date-fns/esm/locale/de";
import { Timestamp, where } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { DateRangePicker } from "react-nice-dates";
import "react-nice-dates/build/style.css";
import Dropdown from "../../components/UI/Dropdown";
import { fullHouseText, ReservationStatus, Room, RoomReservation } from "../../ostertalertypes";
import { useSettingsContext } from "../../SettingsContext";
import { useHouseContext } from "../../HouseContext";
import { fetchAllocationBookings } from "../../utils/database";
import { dateToUTC, DATE_FORMAT, isWithinIntervalExclusive, getFirstDayOfPreviousMonth } from "../../utils/dateutils";
import styles from "./StepSingleRoom.module.css";
import { faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface IBookerRoom {
  availableRooms: Room[];
  roomState: RoomReservation;
  index: number;
  houseName: string;
  enableMyReservation?: boolean; // admin needs to be able to modify an existing booking. e.g. to shorten an existing booking. without this option it would not possible to click it.
  onRoomUpdated: (appartments: RoomReservation, index: number) => void;
}

const StepSingleRoom: React.FC<IBookerRoom> = (props) => {
  const [occupiedDates, setOccupiedDates] = useState<RoomReservation[]>([]);
  const [showRoomInfo, setShowRoomInfo] = useState(false);
  const settingsCtx = useSettingsContext();
  const houseCtx = useHouseContext();

  const house = houseCtx.houses.get(props.houseName.toLowerCase());
  const currentRoom = house?.rooms.get(props.roomState.roomName);
  const maxCapacity = currentRoom?.capacity || house?.capacity || 20;

  function onRoomSelectionChanged(newRoom: string) {
    props.roomState.roomName = newRoom;
    props.roomState.fullHouse = (newRoom === fullHouseText) ? true : false;
    // ensure the roomState itself carries the minimum guest count for full-house
    if (props.roomState.fullHouse) {
      props.roomState.guestCount = 8;
    } else {
      // clear guest count when switching away from full house (optional: keep previous value)
      props.roomState.guestCount = 0;
    }
    // notify parent with the updated roomState (parent will update context/activeBooking)
    props.onRoomUpdated(props.roomState, props.index);
  }

  function getCurrentRoomDescription(): string {
    if (props.roomState.roomName === fullHouseText) {
      return "Gesamtes Haus";
    }
    const room = props.availableRooms.find(r => r.roomName === props.roomState.roomName);
    return room?.description || "Keine Beschreibung verfügbar";
  }

  // Two bookings collide if they overlap. Check-out and check-in on the same day is allowed (room switch at 12 o'clock).
  function overlapsOccupied(from: Timestamp, to: Timestamp): boolean {
    return occupiedDates.some(res => res.from && res.to && from.toMillis() < res.to.toMillis() && to.toMillis() > res.from.toMillis());
  }

  // the picker only greys out days visually, dates can also be typed into the text fields => validate every change.
  function isStartOccupied(from: Timestamp): boolean {
    return occupiedDates.some(res => res.from && res.to && isWithinIntervalExclusive(from.toMillis(), res.from.toMillis(), res.to.toMillis()));
  }

  function onStartDateChange(date: Date | null) {
    const from = date ? Timestamp.fromMillis(dateToUTC(date)) : undefined;
    props.roomState.from = from && isStartOccupied(from) ? undefined : from;
    props.onRoomUpdated(props.roomState, props.index);
  }

  function onEndDateChange(date: Date | null) {
    props.roomState.to = date ? Timestamp.fromMillis(dateToUTC(date)) : undefined;
    // no booking interval may overlap an already existing booking.
    if (props.roomState.from && props.roomState.to && overlapsOccupied(props.roomState.from, props.roomState.to)) {
      props.roomState.to = undefined;
      props.roomState.from = undefined;
    }
    props.onRoomUpdated(props.roomState, props.index);
  }

  const modifiersClassNames = {
    mydisabled: styles.mydisabled,
    occupied: styles.occupied,
    checkin: styles.checkin,
    checkout: styles.checkout,
  };

  const modifiers = {
    // show triangle at checkin
    checkin: (date: Date) => {
      let result = false;
      if (occupiedDates.length > 0) {
        for (let res of occupiedDates) {
          if (res.from && dateToUTC(date) == res.from.toMillis()) {
            result = true;
            // check if this date is also the end of another appointment => suppress triangle => show fully occupied instead
            for (let res2 of occupiedDates) {
              if (res.from?.toMillis() === res2.to?.toMillis()) {
                result = false;
                break;
              }
            }
            break;
          }
        }
      }
      return result;
    },
    // show triangle at checkout
    checkout: (date: Date) => {
      let result = false;
      if (occupiedDates.length > 0) {
        for (let res of occupiedDates) {
          // is checkout date?
          if (res.to && dateToUTC(date) == res.to.toMillis()) {
            result = true;
            // check if this date is also the begin of another appointment => suppress triangle => show fully occupied instead
            for (let res2 of occupiedDates) {
              if (res.to?.toMillis() === res2.from?.toMillis()) {
                result = false;
                break;
              }
            }
            break;
          }
        }
      }
      return result;
    },
    // mark complete date as red
    occupied: (date: Date) => {
      let result = false;
      if (occupiedDates.length > 0) {
        for (let res of occupiedDates) {
          if (res.from && res.to && isWithinInterval(dateToUTC(date), { start: res.from.toMillis(), end: res.to.toMillis() })) {
            result = true;
            // if it's a pure chick in or checkout date => do not show show fully occupied => triangle wins
            if (modifiers.checkout(date) || modifiers.checkin(date)) {
              result = false;
            }
            break;
          }
        }
      }
      return result;
    },
    // "disabled" is the modifier which react-nice-dates evaluates for selection. "mydisabled" is only used for styling.
    disabled: (date: Date) => modifiers.mydisabled(date),
    mydisabled: (date: Date) => {
      let result = false;
      if (occupiedDates.length > 0) {
        for (let res of occupiedDates) {
          // room switch is at same day at 12 o'clock => first day and last day of booking must be selectable.
          if (res.from && res.to && isWithinIntervalExclusive(dateToUTC(date), res.from.toMillis(), res.to.toMillis())) {
            result = true;
            break;
          }
        }
      }
      return result;
    }
  };

  const updateGuestCount = (guestCount: number) => {
    // minimum 8 guests for full house
    if (props.roomState.roomName === fullHouseText && guestCount < 8) {
      return;
    }
    // restrict to maximum house capacity
    if (guestCount > maxCapacity) {
      return;
    }
    props.roomState.guestCount = guestCount < 0 ? 0 : guestCount;
    props.onRoomUpdated(props.roomState, props.index);
  };

  const getGuestCount = () => {
    return props.roomState.guestCount == 0 ? "" : props.roomState.guestCount;
  };



  useEffect(() => {
    const fetchOccupiedDates = async () => {
      if (props.roomState.roomName === "") {
        return;
      }

      const firstDayOfMonth = getFirstDayOfPreviousMonth();
      let roomReservations: RoomReservation[] = []
      // do not search for roomName "Ganzes Haus" => it will be retrieved already by second query.
      if (props.roomState.roomName !== fullHouseText) {
        roomReservations = await fetchAllocationBookings(false, where("to", ">=", firstDayOfMonth),
          where("roomName", "==", props.roomState.roomName),
          where("houseName", "==", props.roomState.houseName),
          where("status", "in", [ReservationStatus.BOOKING_REQUESTED, ReservationStatus.ACCEPTED]))
      } else {
        roomReservations = await fetchAllocationBookings(false, where("to", ">=", firstDayOfMonth),
          // every room must be available to book complete house => show all bookings of the house and do not constrain on any room names
          where("houseName", "==", props.roomState.houseName),
          where("status", "in", [ReservationStatus.BOOKING_REQUESTED, ReservationStatus.ACCEPTED]))
      }

      const fullHouseReservations = await fetchAllocationBookings(false, where("to", ">=", firstDayOfMonth),
        where("fullHouse", "==", true),
        where("houseName", "==", props.roomState.houseName),
        where("status", "in", [ReservationStatus.BOOKING_REQUESTED, ReservationStatus.ACCEPTED]))

      // "to" instead of "from": a running booking which started earlier must still be shown as occupied.
      // needs the composite indexes in firestore.indexes.json
      let allRes = fullHouseReservations.concat(roomReservations);

      // filter out my own reservation => otherwise it can not be changed, because the datepicker would show it as occupied
      if (props.enableMyReservation) {
        allRes = allRes.filter(res => res.id !== props.roomState.id)
      }
      setOccupiedDates(allRes);
    };

    fetchOccupiedDates();
  }, [props.roomState.roomName, props.roomState.id, props.roomState.houseName, props.enableMyReservation]);

  return (
    <div className="mt-5">
      <div className="mb-3 flex space-x-2">
        <Dropdown
          id="roomSelector"
          label="Zimmer auswählen:"
          selected={props.roomState.roomName}
          entries={props.availableRooms.reduce((prev, room) => [...prev, room.roomName], [""])}
          onChange={onRoomSelectionChanged}
        />
        <Button onClick={() => setShowRoomInfo(true)} title="Zimmer Info" disabled={!props.roomState.roomName}>
          <InfoIcon />
        </Button>
      </div>
      <DateRangePicker
        startDate={props.roomState.from?.toDate()}
        endDate={props.roomState.to?.toDate()}
        onStartDateChange={onStartDateChange}
        onEndDateChange={onEndDateChange}
        minimumDate={new Date()}
        maximumDate={settingsCtx.settings && settingsCtx.settings?.reservation.maxAllowedBookingDate.toDate()}
        modifiers={modifiers}
        modifiersClassNames={modifiersClassNames}
        minimumLength={1}
        format={DATE_FORMAT}
        locale={de}
      >
        {({ startDateInputProps, endDateInputProps, focus }) => {
          startDateInputProps.placeholder = "Datum auswählen";
          endDateInputProps.placeholder = "Datum auswählen";
          return (
            <div>
              <TextField className="w-full mb-3" key="anreise" label="Anreise:" id="labelFrom" inputProps={startDateInputProps} />
              <TextField className="w-full mb-3" key="abreise" label="Abreise:" id="labelTo" inputProps={endDateInputProps} />
            </div>
          );
        }}
      </DateRangePicker>
      <div className="flex space-x-2">
        <TextField className="w-full"
          required
          error={props.roomState.guestCount === 0}
          id="outlined-required"
          type="text"
          label="Anzahl Gäste"
          value={getGuestCount()}
          onChange={(e) => updateGuestCount(parseInt((e.target.value.trim())))}
        />
        <div className="flex space-x-1">
          <Button onClick={() => { updateGuestCount(props.roomState.guestCount + 1) }}>
            <FontAwesomeIcon icon={faPlus} />
          </Button>
          <Button onClick={() => { updateGuestCount(props.roomState.guestCount - 1) }}>
            <FontAwesomeIcon icon={faMinus} />
          </Button>
        </div>
      </div>
      {props.roomState.roomName === fullHouseText && (
        <Alert severity="info" className="mt-5">
          Für die Buchung des gesamten Hauses sind mindestens 8 Gäste erforderlich. Sollten weniger als 8 Gäste
          anreisen, so behalten wir uns vor, die fehlenden Gäste zum Übernachtungspreis von Nichtvereinsmitglieder anzusetzen.
          Sollte vor Anreise bekannt sein, dass nicht alle gebuchten Gäste mitreisen, so informieren Sie bitte den Hüttenwart
          rechtzeitig, so dass Zimmer ggf. anderweitig vergeben werden können.
        </Alert>
      )}

      <Dialog open={showRoomInfo} onClose={() => setShowRoomInfo(false)}>
        <DialogTitle>Beschreibung</DialogTitle>
        <DialogContent>
          <p className="mt-2">{getCurrentRoomDescription()}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRoomInfo(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default StepSingleRoom;


